import moment from 'moment';
import type { AssistantAudience } from './assistantCapabilities';
import { extractEquipmentNumber } from './equipmentCheckoutSkill';
import type { KnowledgeAnswer, KnowledgeBreakdownRow, KnowledgeContext, KnowledgeLink, KnowledgeQuery } from './knowledgeRegistry';
import { displayDate, enumLabel, personName, shiftedDay } from './lookupFormat';
import { claimsLookup, extractLabelledEquipmentNumber } from './lookupRouting';
import {
  searchBuyerCurrentInventory,
  searchEquipmentActivity,
  searchSupplierCurrentInventory,
  type EquipmentActivityRow,
  type SupplierCurrentInventoryLookupRow,
} from './requests/equipment-lookup-requests';

/**
 * Where a piece of equipment is, from its gate history: "where is container
 * ABCU1234567", "is trailer 5301 still on site", "has chassis X left", "history
 * of container X", "who checked out trailer 5301".
 *
 * ## Why the latest gate event decides
 *
 * Equipment is on site from its check-in until its check-out, so the most recent
 * event says which side of the gate it is on, and when and where it got there.
 * That is the answer the customer on the phone needs — "your container checked
 * out of Toledo Yard on 09/12 at 3:45 PM with driver J. Smith" — and the recent
 * trail under it shows the pattern (repeated visits, a missing check-out) that
 * explains most disputes about dwell and overage charges.
 *
 * ## Why the match must be exact
 *
 * The interchange searches match an equipment number partially and across
 * container, trailer, chassis and truck-plate numbers, so a search for 5301 also
 * returns trailer 53012 and plate ABC5301. Reporting on those would describe a
 * different trailer with full confidence, so only exact matches are used; near
 * misses are offered back as "did you mean", which catches the usual typo.
 */

/** Events listed under the answer. */
const TRAIL = 8;

/** Near-miss equipment numbers offered back when nothing matches exactly. */
const SUGGESTIONS = 5;

const ACTIVITY_DATETIME = 'MM/DD/YYYY hh:mm A';

/** Case, spaces and dashes are how the same number is typed differently. */
const normalizeEquipment = (value?: string): string => (value ?? '').replace(/[^a-z0-9]/gi, '').toUpperCase();

const numbersOn = (row: EquipmentActivityRow): Array<string | undefined> => [
  row.containerNumber,
  row.trailerNumber,
  row.chassisNumber,
  row.truckLicensePlateNumber,
];

const matchesExactly = (row: EquipmentActivityRow, equipmentNumber: string): boolean =>
  numbersOn(row).some((candidate) => normalizeEquipment(candidate) === normalizeEquipment(equipmentNumber));

/** The number as the gate recorded it, which may be formatted unlike the question. */
const recordedNumber = (row: EquipmentActivityRow, equipmentNumber: string): string =>
  numbersOn(row).find((candidate) => normalizeEquipment(candidate) === normalizeEquipment(equipmentNumber)) ??
  equipmentNumber;

const equipmentNumberIn = (message: string): string | null =>
  extractLabelledEquipmentNumber(message) ?? extractEquipmentNumber(message);

const activityLabel = (activity?: string): string => {
  if (activity === 'CHECK_IN') return 'Check in';
  if (activity === 'CHECK_OUT') return 'Check out';
  return enumLabel(activity) || 'Activity';
};

const bookingPart = (row: EquipmentActivityRow): string => (row.orderNumber ? `booking #${row.orderNumber}` : '');

const driverPart = (row: EquipmentActivityRow): string => {
  const name = personName(row.driverFirstName, row.driverLastName);
  const truck = row.truckLicensePlateNumber?.trim();
  if (name && truck) return `driver ${name}, truck ${truck}`;
  if (name) return `driver ${name}`;
  return truck ? `truck ${truck}` : '';
};

/** Whose equipment it is, for the audiences that see more than their own. */
const ownerPart = (row: EquipmentActivityRow, audience: AssistantAudience): string =>
  audience !== 'buyer' && row.buyerName ? row.buyerName : '';

const trailRow = (row: EquipmentActivityRow, audience: AssistantAudience): KnowledgeBreakdownRow => ({
  label: [row.activityDate?.trim(), activityLabel(row.activity)].filter(Boolean).join(' \u00b7 '),
  value: [
    row.locationName,
    audience === 'admin' ? row.supplierName : '',
    ownerPart(row, audience),
    bookingPart(row),
    driverPart(row),
    row.activityUser ? `by ${row.activityUser}` : '',
    row.correctionStatus === 'PENDING' ? 'correction pending' : '',
  ]
    .map((part) => (part ?? '').trim())
    .filter(Boolean)
    .join(' \u00b7 '),
});

/** "Toledo Yard (Acme Storage)" for staff, who need the supplier as well. */
const placePart = (row: EquipmentActivityRow, audience: AssistantAudience): string => {
  const location = row.locationName ? `**${row.locationName}**` : 'the yard';
  return audience === 'admin' && row.supplierName ? `${location} (${row.supplierName})` : location;
};

/** " (booking #1021445 for Acme Trucking; driver Jo Smith, truck OH-1234)", or ''. */
const detailsFor = (row: EquipmentActivityRow, audience: AssistantAudience): string => {
  const owner = ownerPart(row, audience);
  const booking = [bookingPart(row), owner ? `for ${owner}` : ''].filter(Boolean).join(' ');
  const parts = [booking, driverPart(row)].filter(Boolean);
  return parts.length ? ` (${parts.join('; ')})` : '';
};

const whereaboutsSentence = (
  latest: EquipmentActivityRow,
  equipmentNumber: string,
  audience: AssistantAudience,
): string => {
  const when = latest.activityDate?.trim() ? ` on **${latest.activityDate.trim()}**` : '';
  if (latest.activity === 'CHECK_OUT') {
    const dwell = latest.dwellTimeDisplay?.trim() ? ` It had been on site for ${latest.dwellTimeDisplay.trim()}.` : '';
    return (
      `**${equipmentNumber}** is **not on site** \u2014 it last checked out of ${placePart(latest, audience)}` +
      `${when}${detailsFor(latest, audience)}.${dwell}`
    );
  }
  if (latest.activity === 'CHECK_IN') {
    return (
      `**${equipmentNumber}** is **on site** at ${placePart(latest, audience)} \u2014 it checked in` +
      `${when}${detailsFor(latest, audience)} and has not checked out since.`
    );
  }
  return `The latest gate event for **${equipmentNumber}** is a ${activityLabel(latest.activity).toLowerCase()}${when} at ${placePart(latest, audience)}.`;
};

/** The earliest event shown, as a report start date so none are filtered out. */
const earliestDay = (rows: EquipmentActivityRow[]): string => {
  const days = rows
    .map((row) => moment(row.activityDate ?? '', ACTIVITY_DATETIME))
    .filter((date) => date.isValid());
  if (days.length === 0) return '';
  return moment.min(days).format('MM/DD/YYYY');
};

/**
 * The audience's interchange report filtered to the equipment. Each report opens
 * on the last month by default, so the link starts at the oldest event shown.
 * The buyer report is a tab under Reports and takes its filters as `init…`.
 */
const interchangeLink = (equipmentNumber: string, audience: AssistantAudience, since: string): KnowledgeLink => {
  const encoded = encodeURIComponent(equipmentNumber);
  const date = encodeURIComponent(shiftedDay(since, -1));
  if (audience === 'buyer') {
    return {
      label: 'Open Interchanges',
      path: `/reports/interchanges?equipmentNumber=${encoded}${date ? `&startDate=${date}` : ''}`,
    };
  }
  const base = audience === 'admin' ? '/admin-interchanges' : '/supplier-interchanges';
  return { label: 'Open Interchanges', path: `${base}?equipmentNumber=${encoded}${date ? `&startDate=${date}` : ''}` };
};

const CURRENT_INVENTORY: Partial<
  Record<AssistantAudience, (equipmentNumber: string) => Promise<SupplierCurrentInventoryLookupRow[]>>
> = {
  supplier: searchSupplierCurrentInventory,
  buyer: searchBuyerCurrentInventory,
};

/**
 * Equipment can be on the current-inventory list with no gate event the search
 * returns — moved in by an inventory import, say — so an empty history is
 * checked against it before answering "not found".
 */
const fromCurrentInventory = async (
  equipmentNumber: string,
  audience: AssistantAudience,
): Promise<KnowledgeAnswer | null> => {
  const search = CURRENT_INVENTORY[audience];
  if (!search) return null;
  const rows = await search(equipmentNumber);
  const row = rows.find((candidate) =>
    [candidate.containerNumber, candidate.trailerNumber, candidate.chassisNumber].some(
      (value) => normalizeEquipment(value) === normalizeEquipment(equipmentNumber),
    ),
  );
  if (!row) return null;
  const encoded = encodeURIComponent(equipmentNumber);
  return {
    text:
      `**${equipmentNumber}** is **on site**${row.locationName ? ` at **${row.locationName}**` : ''}` +
      `${displayDate(row.checkInDate) ? ` since **${displayDate(row.checkInDate)}**` : ''}` +
      `${row.orderNumber ? ` on booking **#${row.orderNumber}**` : ''}, according to current inventory.`,
    link:
      audience === 'buyer'
        ? { label: 'Open Current Inventory', path: `/reports/buyer-inventory?equipmentNumber=${encoded}` }
        : { label: 'Open Current Inventory', path: `/supplier-inventory?equipmentNumber=${encoded}` },
  };
};

const WHOSE: Record<AssistantAudience, string> = {
  buyer: 'for your account',
  supplier: 'at your locations',
  admin: 'at any location',
};

export const answerEquipmentWhereabouts = async ({
  message,
  audience,
}: KnowledgeContext): Promise<KnowledgeAnswer> => {
  const asked = equipmentNumberIn(message);
  if (!asked) {
    return {
      text:
        'Which equipment do you mean? Tell me its container, trailer or chassis number \u2014 ' +
        'for example _"where is container ABCU1234567"_ \u2014 and I will check its gate history.',
    };
  }

  const rows = await searchEquipmentActivity(audience, asked);
  const exact = rows.filter((row) => matchesExactly(row, asked));

  if (exact.length === 0) {
    const inventory = await fromCurrentInventory(asked, audience);
    if (inventory) return inventory;

    const similar = Array.from(
      new Set(
        rows.flatMap((row) =>
          numbersOn(row).filter((value): value is string =>
            Boolean(value && normalizeEquipment(value).includes(normalizeEquipment(asked))),
          ),
        ),
      ),
    ).slice(0, SUGGESTIONS);

    return {
      text:
        `I could not find any gate activity for **${asked}** ${WHOSE[audience]}.` +
        (similar.length
          ? ` Did you mean ${similar.map((value) => `**${value}**`).join(', ')}? Ask me about that number.`
          : ' Double-check the number \u2014 it has to match the container, trailer or chassis number the gate recorded.'),
    };
  }

  const equipmentNumber = recordedNumber(exact[0], asked);
  const shown = exact.slice(0, TRAIL);
  const pending = exact[0].correctionStatus === 'PENDING'
    ? ' A correction to that event is waiting for review.'
    : '';
  const count = exact.length;
  const trailNote = count > 1 ? ` Its ${Math.min(count, TRAIL)} most recent gate events are below.` : '';

  return {
    text: `${whereaboutsSentence(exact[0], equipmentNumber, audience)}${pending}${trailNote}`,
    breakdown: count > 1 ? shown.map((row) => trailRow(row, audience)) : [trailRow(exact[0], audience)],
    link: interchangeLink(equipmentNumber, audience, earliestDay(shown)),
  };
};

export const equipmentWhereaboutsQuery: KnowledgeQuery = {
  id: 'equipmentWhereabouts',
  audiences: ['admin', 'supplier', 'buyer'],
  synonyms: [
    'where is my container',
    'where is my trailer',
    'where is my chassis',
    'where is my equipment',
    'where is the container',
    'where is the trailer',
    'is my container on site',
    'is my trailer on site',
    'is my container still in the yard',
    'is my trailer still in the yard',
    'gate history for a container',
    'gate history for a trailer',
  ],
  claims: claimsLookup('equipment'),
  needsLocations: false,
  example: 'where is container ABCU1234567',
  description:
    'Finds where one piece of equipment is by its container, trailer or chassis number (e.g. ' +
    '"where is container ABCU1234567", "is trailer 5301 still on site", "has chassis X left", ' +
    '"who checked out trailer 5301"). Reads its gate history: whether it is on site or gone, ' +
    'at which location, since when, on which booking and with which driver, plus its recent ' +
    'check-ins and check-outs. About one named piece of equipment, not a count of inventory.',
  answer: answerEquipmentWhereabouts,
};

export const __testing = { normalizeEquipment, matchesExactly, earliestDay, equipmentNumberIn };
